'use client';

import { useState } from 'react';

import { DebugPanel } from '@packages/modules/src/DebugPanel/DebugPanel.client';
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@packages/shared/src/ui/ToggleButtonGroup/ToggleButtonGroup';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@packages/shared/src/ui/Tabs/Tabs';
import { Button } from '@packages/shared/src/ui/Button/Button';

import { type SearchParametersInput } from '@packages/gql/generated/shopping/graphql';
import type { TypedDocumentNode } from 'urql';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { SearchDebugPanelFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchDebugPanelFragmentFragmentDoc';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';

import { useAtom, useAtomValue } from 'jotai';
import { CopyableCodeBlock } from './codeBlocks/CopyableCodeBlock';
import { BackChannelRequestDebugInfo } from './backChannelRequest/BackChannelRequestDebugInfo';
import * as features from '../../activeFeatureFlags';
import { hasFeatureFlag } from '../../utils/featureFlags/hasFeatureFlag';
import { getFeatureGroup } from '../../utils/featureFlags/getFeatureGroup';
import type { FeatureFlag, FeatureFlagWithComplexTest } from '../../utils/featureFlags/types';
import { numberOfSeenProductsAtom, showSeenProductsAtom } from '../../atoms/numberOfSeenProducts';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const tabs = ['basics', 'gql', 'eps'] as const;

/* GraphQL */ `
  fragment SearchDebugPanelFragment on ProperCategoryQueryResult {
    currentCategoryId
  }
`;

const FeatureToggle = ({ feature }: { feature: FeatureFlag }) => {
  const { getCookies, setCookie, destroyCookie } = useCookies();

  const getValueFromCookies = () => {
    const state = hasFeatureFlag(feature, getCookies());
    return state ? 'override' : 'default';
  };

  const [value, setValue] = useState(getValueFromCookies());

  const handleChange = (newValue: string) => {
    if (newValue === 'override') {
      setCookie(null, `feature/${feature.name}`, '1', {
        path: '/',
      });
    } else {
      destroyCookie(null, `feature/${feature.name}`, {
        path: '/',
      });
    }

    setValue(getValueFromCookies());
  };

  const options = [
    { value: 'default', label: 'Disabled' },
    {
      value: 'override',
      label: 'Enabled',
    },
  ];

  return (
    <ToggleGroup
      id={`feature-toggle-${feature.name}`}
      type="single"
      label={feature.name}
      value={value}
      onValueChange={handleChange}
    >
      {options.map((option) => (
        <ToggleGroupItem key={option.value} value={option.value}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

const FeatureGroupToggle = ({ feature }: { feature: FeatureFlagWithComplexTest<unknown> }) => {
  const { getCookies, setCookie, destroyCookie } = useCookies();

  const getValueFromCookies = () => {
    const state = getFeatureGroup(feature, getCookies());
    return state === feature.test.featureCookieGroup ? 'override' : 'default';
  };

  const [value, setValue] = useState(getValueFromCookies());

  const handleChange = (newValue: string) => {
    if (newValue === 'override') {
      setCookie(null, `feature/${feature.name}`, '1', {
        path: '/',
      });
    } else {
      destroyCookie(null, `feature/${feature.name}`, {
        path: '/',
      });
    }

    setValue(getValueFromCookies());
  };

  const options = [
    { value: 'default', label: String(feature.test.defaultGroup) },
    {
      value: 'override',
      label: String(feature.test.featureCookieGroup),
    },
  ];

  return (
    <ToggleGroup
      id={`feature-toggle-${feature.name}`}
      type="single"
      label={feature.name}
      value={value}
      onValueChange={handleChange}
    >
      {options.map((option) => (
        <ToggleGroupItem key={option.value} value={option.value}>
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
};

/**
 * Debug panel to assist backend developers and product staff with debugging and analyzing search requests.
 * */
export const SearchDebugPanel = <
  Query,
  Variables extends
    | { params: SearchParametersInput; query: string }
    | { params: SearchParametersInput; pathname: string },
>({
  maskedCategoryResult,
  document,
  data,
  variables,
}: {
  maskedCategoryResult?: FragmentType<typeof SearchDebugPanelFragmentFragmentDoc>;
  document?: TypedDocumentNode<Query, any>; // typing this to Variables is pretty complex, so for now this will have to do
  data?: Query;
  variables?: Variables;
}) => {
  const [activeTab, setActiveTab] = useState<(typeof tabs)[number]>('basics');
  const numberOfSeenProducts = useAtomValue(numberOfSeenProductsAtom);
  const [showSeenProducts, setShowSeenProducts] = useAtom(showSeenProductsAtom);
  const toggleShowSeenProducts = () => {
    setShowSeenProducts((prev) => !prev);
  };

  const categoryResult = unmask<typeof SearchDebugPanelFragmentFragmentDoc>(maskedCategoryResult);

  return (
    <DebugPanel
      addons={
        showSeenProducts && (
          <>
            <p className="text-size-body-sm">{numberOfSeenProducts} seen products.</p>
            <style>
              {`
.product-card.seen {
  position: relative;
}
.product-card.seen::after {
  content: '';
  position: absolute;
  inset: -5px;
  background-color: #00ff0033;
  border-radius: 5px;
}
`}
            </style>
          </>
        )
      }
    >
      <Tabs
        className="w-full"
        value={activeTab}
        onValueChange={(newTab: string) => setActiveTab(newTab as (typeof tabs)[number])}
      >
        <TabsList className="w-full">
          <TabsTrigger className="w-full" value="basics">
            Basics
          </TabsTrigger>
          <TabsTrigger className="w-full" value="gql">
            GraphQL
          </TabsTrigger>
          <TabsTrigger className="w-full" value="eps">
            EPS
          </TabsTrigger>
        </TabsList>

        <div className="overflow-y-auto p-2 [&>pre]:overflow-x-auto [&>pre]:border [&>pre]:border-gray-300 [&>pre]:bg-gray-200">
          <TabsContent value="basics">
            <>
              <h4>Context</h4>
              {categoryResult && (
                <p className="text-size-body-sm">
                  Current Category: {categoryResult.currentCategoryId}
                </p>
              )}
              {!categoryResult && (
                <>
                  <p className="text-size-body-sm">
                    Number of seen products: {numberOfSeenProducts}
                  </p>

                  <div>
                    <Button onClick={toggleShowSeenProducts}>
                      {showSeenProducts ? 'Hide' : 'Show'} seen products
                    </Button>
                  </div>
                </>
              )}
              <h4>Available feature flags</h4>
              <div className="flex flex-col">
                {Object.values(features).map((feature) =>
                  'test' in feature ? (
                    <FeatureGroupToggle key={feature.name} feature={feature} />
                  ) : (
                    <FeatureToggle key={feature.name} feature={feature} />
                  ),
                )}
              </div>
            </>
          </TabsContent>
          <TabsContent value="gql">
            <>
              {document?.loc && (
                <CopyableCodeBlock content={document.loc?.source.body} maxLines={10}>
                  Query
                </CopyableCodeBlock>
              )}
              {data && (
                <CopyableCodeBlock content={data} maxLines={10}>
                  Response
                </CopyableCodeBlock>
              )}
              {variables && <CopyableCodeBlock content={variables}>Variables</CopyableCodeBlock>}
            </>
          </TabsContent>

          <TabsContent value="eps">
            {variables && <BackChannelRequestDebugInfo variables={variables} />}
          </TabsContent>
        </div>
      </Tabs>
    </DebugPanel>
  );
};

// Default export needed for lazy loadin g
// eslint-disable-next-line import-x/no-default-export
export default SearchDebugPanel;
